import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { Stack, Typography, useTheme, darken } from "@mui/material";
import { useSnackbar } from "notistack";

import { ReactComponent as PeopleIcon } from "assets/svg/people.svg";

import { TaskActivityCard } from "user-journey/components/journey-activity/cards/TaskActivityCard";
import { ActivityStatus, JourneyActivityType } from "user-journey/consts/journeyActivity";

import { getInvertColor } from "utils/themeUtils";
import { apiClient } from "utils/apiClient";
import { getErrorMessage } from "utils/errorUtils";

import { localStorageOrgSlugKey } from "auth/helpers/authApi";

import Preloader from "components/PreLoader";

import "./index.css";

interface ExternalTask {
	_id: string;
	dueAt: Date;
	startedAt: string;
	organizationId: string;
	userId: string;
	taskId: string;
	externalAssignees: {
		email: string;
		name: string;
	}[];
	completedAt: string;
	completedBy: string;
	task: {
		_id: string;
		name: string;
		description: string;
	};
	user: {
		_id: string;
		name: string;
		email: string;
		profilePictureUrl: string;
	};
}

export const ExternalTaskPage = () => {
	const params = useParams();

	const { formatMessage } = useIntl();

	const { enqueueSnackbar } = useSnackbar();

	const theme = useTheme();

	const isLight = getInvertColor(theme.palette.primary.main) === "#3C4255";

	const [loading, setLoading] = useState(false);
	const [externalTasks, setExternalTasks] = useState<ExternalTask[]>([]);

	const getExternalTasks = useCallback(async () => {
		setLoading(true);
		try {
			const response = await apiClient<ExternalTask[]>(`user-tasks/external/${params.base64Email}`);

			if (response && response.data.length > 0) {
				setExternalTasks(response.data);
			}
		} catch (error) {
			const errorJson = (error as any)?.json;
			let errorMessage = formatMessage({
				id: "DASHBOARD.employee-journey.create_error",
			});
			if (errorJson.code) {
				errorMessage = getErrorMessage(errorJson.code);
			}
			enqueueSnackbar(errorMessage, { variant: "error" });
		} finally {
			setLoading(false);
		}
	}, [formatMessage, enqueueSnackbar, params]);

	useEffect(() => {
		if (params.slug) {
			localStorage.setItem(localStorageOrgSlugKey, "hogwarts-2");
		}

		if (params.base64Email) {
			getExternalTasks();
		}
	}, [params, getExternalTasks]);

	return (
		<Stack
			id="ExternalTask"
			alignItems="center"
			justifyContent="center"
			p="2rem"
			bgcolor="legacyPrimary.100"
			height="100%"
		>
			<Helmet>
				<title>
					{formatMessage({
						id: "EXTERNAL.user-task.header",
						defaultMessage: "Header",
					})}
				</title>
			</Helmet>
			<Stack gap="2.4rem" p="2.4rem" width="90%" bgcolor="common.white" borderRadius="0.8rem">
				<Stack flexDirection="row" alignItems="center" gap="1.2rem">
					<PeopleIcon width="4rem" height="3.2rem" />
					<Stack>
						<Typography
							component="h1"
							fontWeight={800}
							fontSize="2.2rem"
							color={(theme) =>
								isLight ? darken(theme.palette.primary.main, 0.5) : theme.palette.primary.main
							}
						>
							{formatMessage({ id: "EXTERNAL.user-task.header", defaultMessage: "Header" })}
						</Typography>
						<Typography fontSize="1.8rem" color="grey.600">
							{formatMessage({
								id: "EXTERNAL.user-task.description",
								defaultMessage:
									"Lorem ipsum dolor sit, amet consectetur adipisicing elit. Unde voluptates sint aliquam, ipsa dolor praesentium. Modi quasi nobis, laudantium omnis, consequatur rerum quod voluptates, natus fuga quidem sequi itaque dolorem.",
							})}
						</Typography>
					</Stack>
				</Stack>
				<Stack
					position="relative"
					flexDirection="row"
					alignItems="center"
					gap="1.6rem"
					height="34rem"
					className="scrollbar"
					sx={{ overflowX: "auto", scrollbarGutter: "stable" }}
				>
					{loading ? (
						<Preloader />
					) : (
						externalTasks.map((task) => (
							<TaskActivityCard
								key={task._id}
								// @ts-ignore
								activity={{
									dueDate: task.dueAt,
									title: task.task.name,
									status: task.completedAt ? ActivityStatus.COMPLETED : ActivityStatus.IN_PROGRESS,
									type: JourneyActivityType.TASK,
									description: task.task.description,
									userTaskId: task._id,
									user: {
										_id: task.user._id,
										name: task.user.name,
										email: task.user.email,
										profilePictureUrl: task.user.profilePictureUrl,
									},
								}}
								refetchExternalTasks={getExternalTasks}
							/>
						))
					)}
				</Stack>
			</Stack>
		</Stack>
	);
};
